import { AlertPrioritySchedulePayload } from 'src/app/services/site.service';
import { AlertPriority, toAlertPriority } from './alert-priority';

/**
 * Time-based alert priority: a site's recurring windows, and the shape the Cameras drawer
 * edits them in.
 *
 * A window overrides a camera's own priority for the hours it covers and nothing else —
 * outside every window the camera's level is stamped exactly as it always was. So a site
 * with no windows is unchanged, which is why nothing here has a "default" to fall back to.
 *
 * A window is a level and two times, and deliberately nothing else. It runs every day and
 * covers every camera at the site: WHICH cameras sit at which level is already the three
 * lists in the drawer, and the window hangs off the level's own row there — asking the
 * same question twice on one panel is how the two answers end up disagreeing. The server
 * still stores a day and camera scope — see alert-priority-schedule.model.js — and reads
 * an empty one as "all", so narrowing a window later is a UI change on top of a shape that
 * already carries it.
 *
 * One window per level, because that is what the drawer can express: the level is not a
 * field on the row there, it is the row the window is attached to. The server holds a
 * list, so a second window for a level is storable and simply would not be shown.
 *
 * Times are wall-clock "HH:mm" in the platform's timezone, never instants. A window whose
 * end is before its start crosses midnight (20:00-07:00) and the server reads it as
 * covering the small hours of the NEXT day.
 */

/** One row as the drawer holds it while being edited. */
export interface PriorityScheduleRow {
  id: string | null;
  priority: AlertPriority;
  startTime: string;
  endTime: string;
  /**
   * The cameras this level holds while the window is live — the level's own camera list,
   * stored here rather than on the cameras themselves.
   *
   * A camera carries ONE `alert_priority`, so it can only ever be at one level that has no
   * hours. Letting it be High for two hours and Medium for two others means the assignment
   * has to hang off the window, which is the only thing there is one of per level per
   * stretch of the day. Empty means every camera at the site.
   *
   * Held as `camera.name`, never `display_name`: an alert carries the value that matched
   * `camera.name`, and that is what the server joins these against.
   */
  cameras: string[];
  isActive: boolean;
}

// Seconds optional coming in, always written going out — windows saved before the picker
// offered seconds are stored as "20:00", and rejecting those would make an old rule
// unreadable rather than just imprecise. Mirrors TIME_PATTERN in the API's
// alert-priority-schedule.services.js; the two have to agree or the drawer offers a time
// the server refuses.
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

export function isValidWindowTime(value: string): boolean {
  return TIME_PATTERN.test(`${value ?? ''}`.trim());
}

/** Second of the day, or null. Mirrors `toSeconds` in the API service. */
export function windowTimeToSeconds(value: string): number | null {
  const match = TIME_PATTERN.exec(`${value ?? ''}`.trim());
  if (!match) {
    return null;
  }
  return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] ?? 0);
}

/**
 * "20:00:30" <-> a Date, because PrimeNG's time picker binds a Date and these are times of
 * day with no date at all.
 *
 * The date part is fixed rather than today's: `p-datepicker` writes the whole Date back,
 * and seeding it with "now" meant a window edited across midnight came back a day later
 * and compared unequal to the value the operator had not touched.
 */
export function windowTimeToDate(value: string): Date | null {
  const match = TIME_PATTERN.exec(`${value ?? ''}`.trim());
  if (!match) {
    return null;
  }
  const date = new Date(2000, 0, 1, 0, 0, 0, 0);
  date.setHours(Number(match[1]), Number(match[2]), Number(match[3] ?? 0), 0);
  return date;
}

export function dateToWindowTime(value: Date | null | undefined): string {
  if (!(value instanceof Date) || isNaN(value.getTime())) {
    return '';
  }
  return [value.getHours(), value.getMinutes(), value.getSeconds()]
    .map((part) => `${part}`.padStart(2, '0'))
    .join(':');
}

/**
 * Why a row cannot be saved, or null when it can.
 *
 * Equal times are rejected rather than read as "all day": a window from 08:00 to 08:00 is
 * as likely to be a half-finished edit as a deliberate 24 hours, and the server refuses it
 * too — this just says so before the round trip.
 */
export function priorityScheduleRowError(row: PriorityScheduleRow): string | null {
  const start = windowTimeToSeconds(row?.startTime);
  const end = windowTimeToSeconds(row?.endTime);
  if (start === null) {
    return 'Start time is required';
  }
  if (end === null) {
    return 'End time is required';
  }
  // Compared as seconds, not as strings: "20:00" and "20:00:00" are the same instant and
  // a string compare would let that pair through as a zero-length window.
  if (start === end) {
    return 'Start and end cannot be the same';
  }
  if (!toAlertPriority(row?.priority)) {
    return 'Priority is required';
  }
  return null;
}

/** A blank window for one level. 20:00-07:00 because overnight is what these are set for. */
export function emptyPriorityScheduleRow(priority: AlertPriority): PriorityScheduleRow {
  return {
    id: null,
    priority,
    startTime: '20:00:00',
    endTime: '07:00:00',
    cameras: [],
    isActive: true,
  };
}

export function toPriorityScheduleRow(raw: any): PriorityScheduleRow {
  return {
    id: `${raw?.id ?? ''}`.trim() || null,
    priority: toAlertPriority(raw?.priority) ?? 'high',
    startTime: `${raw?.start_time ?? ''}`.trim(),
    endTime: `${raw?.end_time ?? ''}`.trim(),
    cameras: Array.isArray(raw?.cameras)
      ? raw.cameras.map((camera: any) => `${camera?.name ?? ''}`.trim()).filter(Boolean)
      : [],
    isActive: raw?.is_active !== false,
  };
}

/**
 * `days` is left off the body entirely rather than sent empty: the drawer's windows run
 * every day, the server normalises a missing key to "all", and sending [] would wipe a
 * weekday scope set by anything else that ever writes these rules.
 *
 * `cameras` is the opposite — it IS what the drawer edits, so it is always sent.
 */
export function toPrioritySchedulePayload(row: PriorityScheduleRow): AlertPrioritySchedulePayload {
  return {
    id: row.id,
    start_time: `${row.startTime ?? ''}`.trim(),
    end_time: `${row.endTime ?? ''}`.trim(),
    priority: row.priority,
    is_active: row.isActive !== false,
    // Always sent, even empty: the level's list IS this field now, so leaving it off
    // would make the server keep a scope the operator has just cleared.
    cameras: (row.cameras || []).map((name) => ({ id: null, name })),
  };
}

/** True when the window runs past midnight, which the row shows as a "+1 day" hint. */
export function crossesMidnight(row: PriorityScheduleRow): boolean {
  const start = windowTimeToSeconds(row?.startTime);
  const end = windowTimeToSeconds(row?.endTime);
  return start !== null && end !== null && start > end;
}

const SECONDS_PER_DAY = 24 * 60 * 60;

/**
 * A window as one `[from, to)` span on a 24-hour line, or two when it runs past midnight.
 *
 * Mirrors `weeklySegments` in the API's alert-priority-schedule.services.js, minus the
 * weekday axis — the drawer's windows always run every day, so the week collapses to one
 * day. The server stays the gate; this is only here so the operator is told before the
 * round trip instead of by a toast afterwards.
 */
function daySegments(row: PriorityScheduleRow): Array<[number, number]> {
  const start = windowTimeToSeconds(row?.startTime);
  const end = windowTimeToSeconds(row?.endTime);
  if (start === null || end === null || start === end) {
    return [];
  }
  return start < end
    ? [[start, end]]
    : [[start, SECONDS_PER_DAY], [0, end]];
}

/**
 * Whether two every-day windows share any moment.
 *
 * Time only, matching the server. Cameras used to be part of this, and that is what let
 * two levels hold identical hours whenever their lists were empty — "High 5-6 AM" saved
 * beside "Medium 5-6 AM". The three windows are read down the panel as one timetable, so
 * they have to be disjoint whatever cameras they hold.
 */
export function windowsOverlap(left: PriorityScheduleRow, right: PriorityScheduleRow): boolean {
  const leftSpans = daySegments(left);
  const rightSpans = daySegments(right);
  return leftSpans.some(([leftFrom, leftTo]) =>
    rightSpans.some(([rightFrom, rightTo]) => leftFrom < rightTo && rightFrom < leftTo)
  );
}

/**
 * Why this window cannot sit alongside the others, or null when it can.
 *
 * A camera can be High for one stretch of the day and Medium for another — what it cannot
 * be is both at once, so two levels' hours may not overlap. `others` is keyed by level and
 * the level being edited is skipped, because a window never clashes with itself.
 */
export function windowOverlapError(
  row: PriorityScheduleRow,
  others: Record<string, PriorityScheduleRow | null>
): string | null {
  const clash = Object.entries(others || {}).find(
    ([level, other]) => level !== row.priority && !!other && windowsOverlap(row, other as PriorityScheduleRow)
  );
  if (!clash) {
    return null;
  }
  const [level, other] = clash;
  return `These hours overlap ${level} (${formatWindowRange(other as PriorityScheduleRow)}). Give them different hours.`;
}

/**
 * "20:00:30" -> "8:00:30 PM", for the collapsed summary beside a level.
 *
 * Written out rather than run through DatePipe: LOCALE_ID here is fr-FR, which would
 * render these as 20:00 — and the picker the operator just set shows a 12-hour clock, so
 * the summary has to match what they saw rather than the app locale.
 *
 * :00 seconds are dropped. Most windows land on the minute and "8:00:00 PM → 7:00:00 AM"
 * does not fit the column beside the camera list; a window that does use seconds shows
 * them, which is the only case where they carry information.
 */
export function formatWindowTime(value: string): string {
  const match = TIME_PATTERN.exec(`${value ?? ''}`.trim());
  if (!match) {
    return '';
  }
  const hours = Number(match[1]);
  const seconds = Number(match[3] ?? 0);
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const tail = seconds ? `:${`${seconds}`.padStart(2, '0')}` : '';
  return `${hour12}:${match[2]}${tail} ${suffix}`;
}

/** "8:00 PM → 7:00 AM" — the whole window in the space beside a camera list. */
export function formatWindowRange(row: PriorityScheduleRow): string {
  if (!row) {
    return '';
  }
  return `${formatWindowTime(row.startTime)} → ${formatWindowTime(row.endTime)}`;
}
